"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@/components/Button";
import DonationForm from "@/components/DonationForm";
import FlagStrip from "@/components/FlagStrip";
import Icon from "@/components/Icon";
import Modal from "@/components/Modal";
import Text from "@/components/Text";
import classNames from "@/helpers/classNames";
import { ANALYTICS_EVENT, analytics } from "@/tools/analytics";
import type { IDonationPopUpDocument } from "@/tools/sanity/schema/documents/donationPopUp";

import styles from "./styles.module.scss";

export interface DonationPopUpProps {
  /**
   * The resolved pop-up, as the document projections return it. Null or
   * missing means the editor didn't set one — the component renders nothing,
   * so a template can hand it over unconditionally.
   */
  popUp?: IDonationPopUpDocument | null;
  className?: string;
}

const DonationPopUp = (props: DonationPopUpProps) => {
  const { popUp, className } = props;

  const sentinelRef = useRef<HTMLDivElement>(null);

  // The shared Modal renders no dialog semantics and doesn't move focus, so
  // opening the panel would leave a keyboard or screen-reader user on the CTA
  // behind the overlay. Done through a callback ref rather than an effect keyed
  // on `isOpen`, because Modal only mounts its children once it opens — the
  // same reason NewsletterModal does it this way.
  const panelRef = useCallback((node: HTMLDivElement | null) => {
    node?.focus();
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // Same sentinel trick as ChildSponsorBar. It sits in normal flow wherever the
  // template renders this component; while it's still below the viewport the
  // supporter is inside the page body and the bar stays up. Once it crosses in,
  // they've reached the footer and the bar slides away rather than covering it.
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) {
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) {
          return;
        }
        const sentinelTop = entry.boundingClientRect.top;
        const viewportHeight = entry.rootBounds?.height ?? window.innerHeight;
        setIsHidden(sentinelTop <= viewportHeight);
      },
      { threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  const donationForm = popUp?.donationForm;

  // No pop-up chosen, or one saved without its variant — either way there's
  // nothing chargeable to show, and a bar that opens onto an empty form is
  // worse than no bar.
  if (!popUp || !donationForm?.productVariant) {
    return null;
  }

  const { closedTitle, ctaLabel, openTitle, content, taxNote } = popUp;
  const {
    productVariant,
    regularGivingVariant,
    allowRegularGiving,
    addDollarHandles,
    dollarHandles,
    defaultDollarHandle,
    defaultVariantAmount,
    defaultRegularGivingAmount,
  } = donationForm;

  // Only forward the recurring variant and the handle override when the editor
  // actually turned them on — the same gating the donation sections apply.
  const rgVariant = allowRegularGiving ? regularGivingVariant : undefined;
  const handles = addDollarHandles ? dollarHandles : undefined;
  const defaultHandle = addDollarHandles ? defaultDollarHandle : undefined;

  // Fired on open rather than on donate, so the bar's own funnel is measurable
  // separately from the donation it may lead to.
  const handleOpen = () => {
    setIsOpen(true);
    analytics.track({
      event: ANALYTICS_EVENT.DONATION_POP_UP_OPEN,
      payload: { pop_up_id: popUp._id, pop_up_title: openTitle },
    });
  };

  return (
    <>
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
      <aside
        className={classNames(
          styles.container,
          { [styles.open]: isOpen },
          { [styles.hidden]: isHidden && !isOpen },
          className,
        )}
        data-theme="white"
      >
        {/* Hidden, the bar only slides below the fold, so its button stayed in
            the tab order. `inert` on the call to action alone, so the modal
            inside the bar is never affected. */}
        <div className={styles.callToAction} inert={isHidden && !isOpen}>
          {closedTitle && (
            <Text
              className={styles.callToActionText}
              text={closedTitle}
              variant="heading"
              size="sm"
            />
          )}
          <Button
            className={styles.callToActionButton}
            text={ctaLabel}
            theme="yellow"
            size="md"
            onClick={handleOpen}
          />
        </div>

        <div className={styles.flagStripContainer}>
          <FlagStrip className={styles.flagStrip} variant="vibrant" />
        </div>

        <Modal
          hasInnerDialog
          className={styles.modal}
          show={isOpen}
          onClose={() => setIsOpen(false)}
          from="bottom"
          size="full"
        >
          <div
            ref={panelRef}
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label={openTitle}
            tabIndex={-1}
          >
            <div className={styles.panelHeader}>
              {/* A visual grab affordance that duplicates the close button
                  beside it. Kept clickable but out of the tab order and the
                  accessibility tree, so the panel offers one close control
                  rather than two identically-named ones. */}
              <button
                type="button"
                className={styles.dragBar}
                onClick={() => setIsOpen(false)}
                tabIndex={-1}
                aria-hidden="true"
              >
                <span className={styles.dragBarIndicator} />
              </button>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <Icon title="closeBold" size="md" />
              </button>
            </div>

            {/* The form owns the two-column layout: its rows pair the copy
                on the left with the controls on the right, and only it can
                keep the two aligned. */}
            <DonationForm
              className={styles.form}
              variant="popUp"
              title={openTitle}
              content={content}
              disclaimer={taxNote}
              productVariant={productVariant}
              regularGivingVariant={rgVariant}
              dollarHandles={handles}
              defaultDollarHandle={defaultHandle}
              defaultVariantAmount={defaultVariantAmount}
              defaultRegularGivingAmount={defaultRegularGivingAmount}
              onDonationAdded={() => setIsOpen(false)}
            />

            <div className={styles.panelFlagStrip}>
              <FlagStrip className={styles.flagStrip} variant="vibrant" />
            </div>
          </div>
        </Modal>
      </aside>
    </>
  );
};

export default DonationPopUp;
